import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Languages } from "lucide-react";
import { LOGO } from "@/lib/brand";
import { useLang } from "@/lib/i18n";

const NAV_LINKS = [
  { label: "Legacy", href: "#legacy" },
  { label: "Manifesto", href: "#manifesto" },
  { label: "Studio", href: "#studio" },
  { label: "Catalog", href: "#catalog" },
  { label: "Infrastructure", href: "#infrastructure" },
  { label: "Contact", href: "#contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { lang, setLang, t, languages } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        data-testid="site-nav"
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1], delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-[#F4F4F0]/85 backdrop-blur-md border-b border-black/90" : "bg-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-3.5 flex items-center justify-between">
          <a href="#top" data-testid="nav-logo" className="flex items-center group shrink-0">
            <img
              src={LOGO.horizontal}
              alt="Arvind Printers — Design, Brand, Print"
              className="h-10 md:h-12 lg:h-[52px] w-auto max-w-[220px] md:max-w-[255px] object-contain object-left transition-transform duration-500 group-hover:-translate-y-0.5"
            />
          </a>

          <nav className="hidden lg:flex items-center gap-8 font-mono text-[11px] tracking-[0.25em] uppercase">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} data-testid={`nav-link-${l.label.toLowerCase()}`} className="nav-link text-black/80 hover:text-black">{l.label}</a>
            ))}
          </nav>

          <a href="#contact" data-testid="nav-cta-quote" className="hidden lg:inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase border border-black px-4 py-2 hover:bg-black hover:text-[#F4F4F0] transition-colors">
            {t("nav.cta_quote")} <ArrowUpRight className="w-3.5 h-3.5" />
          </a>

          <label className="ml-3 hidden md:flex items-center gap-2 border border-black px-2.5 py-2 bg-[#F4F4F0]/75">
            <Languages className="w-3.5 h-3.5 shrink-0" />
            <select data-testid="nav-lang-select" value={lang} onChange={(e) => setLang(e.target.value)} aria-label="Select website language" className="bg-transparent outline-none font-sans text-sm font-semibold cursor-pointer pr-1">
              {languages.map((item) => <option key={item.code} value={item.code}>{item.native}</option>)}
            </select>
          </label>

          <button data-testid="nav-mobile-toggle" onClick={() => setOpen(true)} className="lg:hidden inline-flex items-center justify-center w-10 h-10 border border-black" aria-label="Open menu">
            <Menu className="w-4 h-4" />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div data-testid="mobile-menu" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] bg-[#0A0A0A] text-[#F4F4F0] flex flex-col">
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/20">
              <img src={LOGO.horizontal} alt="Arvind Printers — Design, Brand, Print" className="h-10 w-auto max-w-[220px] object-contain object-left" />
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="w-10 h-10 border border-white flex items-center justify-center" aria-label="Close menu"><X className="w-4 h-4" /></button>
            </div>
            <nav className="flex-1 flex flex-col justify-center gap-2 px-6">
              {NAV_LINKS.map((l, i) => (
                <motion.a key={l.href} href={l.href} onClick={() => setOpen(false)} initial={{ x: -30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.05 * i + 0.1 }} className="font-display italic text-5xl border-b border-white/10 py-4">{l.label}.</motion.a>
              ))}
              <label className="mt-6 self-start inline-flex items-center gap-3 border border-white px-4 py-3">
                <Languages className="w-4 h-4" />
                <select data-testid="mobile-lang-select" value={lang} onChange={(e) => setLang(e.target.value)} aria-label="Select website language" className="bg-[#0A0A0A] text-[#F4F4F0] outline-none font-sans text-base font-semibold cursor-pointer">
                  {languages.map((item) => <option key={item.code} value={item.code}>{item.native} — {item.label}</option>)}
                </select>
              </label>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
